import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import ICart from "../models/ICart";
import CartService from "../services/CartService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";

export interface ICartContext {
    cart: ICart | null;
    isLoading: boolean;
    refreshCart: () => Promise<void>;
}

const CartContext = createContext<ICartContext | undefined>(undefined);

export interface CartProviderProps {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel?: IVendorModel;
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ userModel, supplierModel, vendorModel, children }) => {
    const [cart, setCart] = useState<ICart | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const refreshCart = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await CartService.getCart(userModel, supplierModel, vendorModel);
            setCart(response.cart);
        } catch (error) {
            console.error("Failed to get cart:", error);
        } finally {
            setIsLoading(false);
        }
    }, [userModel, supplierModel, vendorModel]);

    return React.createElement(CartContext.Provider, { value: { cart, isLoading, refreshCart } }, children);
};

export const useCart = (): ICartContext => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};

export default CartContext;
